/**
 * Carbon contained tabs (DESIGN.md "Navigation"): 48px tabs on layer-accent,
 * split by border-strong, for a table's status or severity. The selected tab
 * joins the layer-01 surface below it, turns 600 and gets a 2px
 * interactive-line bar on top. A count renders as an 18px tag and joins the
 * accessible name ("Open, 6"). ARIA tabs with roving tabindex: the arrow keys,
 * Home and End move and select. Selection is explicit (`value`), never derived.
 * Carbon's tabs switch panels; when the "tabs" only filter one table there is
 * no panel, so `semantics="pressed"` renders them as aria-pressed buttons in a
 * group (mockup 02's `.ctabs`). The look is the same.
 */
import { useRef, type KeyboardEvent, type ReactNode } from 'react'
import clsx from 'clsx'
import { Tag } from './Tag'

export interface ContainedTab {
  id: string
  label: ReactNode
  /** A count tag after the label. Null or undefined hides it. */
  count?: number | string | null
  disabled?: boolean
}

export interface ContainedTabsProps {
  tabs: ContainedTab[]
  /** The selected tab's id. */
  value: string
  onChange: (id: string) => void
  'aria-label': string
  /**
   * tabs (default): ARIA tabs with roving tabindex, for tabs that switch panels. pressed: a group of aria-pressed
   * buttons, each in the tab order, for tabs that only filter the one table below them (mockup 02's `.ctabs`).
   */
  semantics?: 'tabs' | 'pressed'
  className?: string
}

export function ContainedTabs({ tabs, value, onChange, 'aria-label': ariaLabel, semantics = 'tabs', className }: ContainedTabsProps) {
  const refs = useRef(new Map<string, HTMLButtonElement>())
  const asTabs = semantics === 'tabs'
  // One tab is always in the tab order, even when `value` matches none.
  const tabStop = tabs.some((t) => t.id === value) ? value : tabs.find((t) => !t.disabled)?.id

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const enabled = tabs.filter((t) => !t.disabled)
    const at = enabled.findIndex((t) => t.id === value)
    let next: ContainedTab | undefined
    if (e.key === 'ArrowRight') next = enabled[(at + 1) % enabled.length]
    else if (e.key === 'ArrowLeft') next = enabled[(at - 1 + enabled.length) % enabled.length]
    else if (e.key === 'Home') next = enabled[0]
    else if (e.key === 'End') next = enabled[enabled.length - 1]
    if (!next) return
    e.preventDefault()
    onChange(next.id)
    refs.current.get(next.id)?.focus()
  }

  return (
    <div
      role={asTabs ? 'tablist' : 'group'}
      aria-label={ariaLabel}
      onKeyDown={asTabs ? onKeyDown : undefined}
      className={clsx('flex flex-none overflow-x-auto [scrollbar-width:none]', className)}
    >
      {tabs.map((tab) => {
        const selected = tab.id === value
        return (
          <button
            key={tab.id}
            ref={(el) => {
              if (el) refs.current.set(tab.id, el)
              else refs.current.delete(tab.id)
            }}
            type="button"
            role={asTabs ? 'tab' : undefined}
            aria-selected={asTabs ? selected : undefined}
            aria-pressed={asTabs ? undefined : selected}
            tabIndex={asTabs ? (tab.id === tabStop ? 0 : -1) : undefined}
            disabled={tab.disabled}
            onClick={() => onChange(tab.id)}
            className={clsx(
              'relative flex h-12 flex-none items-center gap-2 whitespace-nowrap border-r px-4 text-body last:border-r-0',
              'transition-colors duration-110 ease-carbon disabled:cursor-not-allowed disabled:text-text-placeholder',
              selected
                ? "border-transparent bg-layer-1 font-semibold text-text-primary before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-interactive-line before:content-['']"
                : 'border-line-strong bg-layer-accent text-text-secondary hover:bg-layer-hover hover:text-text-primary',
            )}
          >
            <span>{tab.label}</span>
            {tab.count != null ? (
              <>
                <span className="sr-only">, </span>
                <Tag size="sm" className="tabular-nums">
                  {tab.count}
                </Tag>
              </>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

export default ContainedTabs
